import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import NewsCard from '../components/NewsCard'
import { api } from '../api'
import type { ContentItem } from '../api/apiClient'
import {
  collectContentTags,
  getContentHeroImage,
  toNewsCardItem,
  type NewsCardItem,
  isContentExpired,
} from '../utils/content'
import { NEWS_FALLBACK_ITEMS } from '../data/newsFallback'
import { usePageMetadata } from '../hooks/usePageMetadata'

const fallbackFeatureImage = '/images/home/history-news-hero.jpg'

type LoadingState = 'idle' | 'loading' | 'ready' | 'error'

const News = () => {
  const [items, setItems] = useState<ContentItem[]>([])
  const [loadingState, setLoadingState] = useState<LoadingState>('idle')
  const [activeTag, setActiveTag] = useState<string>('all')
  const [search, setSearch] = useState('')

  usePageMetadata({
    title: 'History in the News | Places in Time',
    description:
      'Follow archaeology, discoveries, heritage stories, and historical context through current news connected to places, figures, and events.',
    path: '/news',
  })

  useEffect(() => {
    let cancelled = false
    setLoadingState('loading')

    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    const todayStartMs = todayStart.getTime()

    api
      .listNews({ limit: 40, status: 'published', sort: 'published_at', order: 'desc' })
      .then((data) => {
        if (cancelled) return
        const freshItems = Array.isArray(data)
          ? data.filter((item) => !isContentExpired(item, todayStartMs))
          : []
        setItems(freshItems)
        setLoadingState('ready')
      })
      .catch(() => {
        if (!cancelled) {
          setLoadingState('error')
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const normalizedSearch = search.trim().toLowerCase()

  const filteredItems = useMemo(() => {
    if (items.length === 0) {
      return []
    }

    return items.filter((item) => {
      const matchesTag = activeTag === 'all' || item.tags?.includes(activeTag)
      if (!matchesTag) {
        return false
      }

      if (!normalizedSearch) {
        return true
      }

      const haystack = `${item.title ?? ''} ${item.summary ?? ''} ${item.excerpt ?? ''}`.toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [items, activeTag, normalizedSearch])

  const tags = useMemo(() => collectContentTags(items), [items])

  const useFallbackData = items.length === 0
  const featureSource = useFallbackData ? null : filteredItems[0] ?? items[0] ?? null
  const featureCard: NewsCardItem = useFallbackData
    ? NEWS_FALLBACK_ITEMS[0]
    : featureSource
      ? toNewsCardItem(featureSource, featureSource.slug ? `/news/${featureSource.slug}` : '/news')
      : NEWS_FALLBACK_ITEMS[0]

  const featureImage = featureSource
    ? getContentHeroImage(featureSource) ?? fallbackFeatureImage
    : fallbackFeatureImage

  const remainderItems = useFallbackData
    ? []
    : featureSource
      ? filteredItems.slice(1)
      : filteredItems

  const newsCards: NewsCardItem[] = useFallbackData
    ? NEWS_FALLBACK_ITEMS.slice(1)
    : remainderItems.map((item) => toNewsCardItem(item, item.slug ? `/news/${item.slug}` : '/news'))

  const noMatches =
    !useFallbackData &&
    items.length > 0 &&
    filteredItems.length === 0 &&
    (activeTag !== 'all' || normalizedSearch.length > 0)

  const featureCtaLabel = featureCard.ctaLabel ?? (featureCard.link ? 'Read update' : 'View story')
  const renderFeatureCta = () => {
    if (featureCard.link) {
      return (
        <a className="button primary" href={featureCard.link} target="_blank" rel="noopener noreferrer">
          {featureCtaLabel}
        </a>
      )
    }

    if (featureCard.to) {
      return (
        <Link className="button primary" to={featureCard.to}>
          {featureCtaLabel}
        </Link>
      )
    }

    return null
  }

  return (
    <section className="news-page" aria-live="polite">
      <header className="news-leading-panel">
        <div className="page-header news-page-header">
          <div className="page-header-title">
            <p className="eyebrow">History News</p>
            <h1>Dispatches from the frontline of history</h1>
          </div>
          <div className="page-header-body">
            <p>
              News items, books, podcasts and videos all curated for you.
              Filter by editorial tags or search by keyword to surface specific updates.
            </p>
          </div>
        </div>

        <div className="news-toolbar" role="group" aria-label="Filter history newsroom">
          <div className="news-tags-cluster">
            <div className="news-tags" aria-label="Filter by tag">
              <button
                type="button"
                className={activeTag === 'all' ? 'tag-filter is-active' : 'tag-filter'}
                onClick={() => setActiveTag('all')}
                aria-pressed={activeTag === 'all'}
              >
                All
              </button>
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className={activeTag === tag ? 'tag-filter is-active' : 'tag-filter'}
                  onClick={() => setActiveTag(tag)}
                  aria-pressed={activeTag === tag}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div className="news-search">
            <label htmlFor="news-search-input">Search the archive</label>
            <div className="news-search-field">
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                <path d="M10.5 3a7.5 7.5 0 0 1 5.98 12.06l4.2 4.2-1.42 1.42-4.2-4.2A7.5 7.5 0 1 1 10.5 3zm0 2a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11z" />
              </svg>
              <input
                id="news-search-input"
                type="search"
                placeholder="Search stories, books, podcasts..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      <article className="news-feature">
        {featureImage && (
          <div className="news-feature-media">
            <img src={featureImage} alt="Featured news visual" loading="lazy" />
          </div>
        )}
        <div className="news-feature-body">
          <span className="news-meta">
            <strong>{featureCard.dateLabel ?? 'Coming soon'}</strong>
            <span>{featureCard.category ?? 'Update'}</span>
          </span>
          <h2>{featureCard.title}</h2>
          <p>{featureCard.summary}</p>
          <div className="button-row">
            {renderFeatureCta()}
            <Link className="button" to="/chat">
              Talk to history
            </Link>
          </div>
        </div>
      </article>

      {loadingState === 'loading' && (
        <p className="news-status">Loading live feed…</p>
      )}
      {loadingState === 'error' && (
        <p className="news-status news-status-error">Live feed unavailable. Showing static newsroom highlights.</p>
      )}

      {noMatches ? (
        <p className="news-status">No news matches those filters. Reset above to see the full feed.</p>
      ) : (
        <div className="news-grid">
          {newsCards.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  )
}

export default News
